import React from 'react';

const PrivacyPolicy: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto bg-white shadow-lg rounded-lg p-8">
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">Privacy Policy</h1>
          <p className="text-sm text-gray-600">Last Updated: October 17, 2025</p>
        </div>

        <div className="prose prose-gray max-w-none">
          {/* 1. Information We Collect */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">1. Information We Collect</h2>

            <h3 className="text-xl font-medium text-gray-800 mb-3">1.1 Information You Provide</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Account Information:</strong> Name, email address, and company affiliation provided during registration</li>
              <li><strong>Conversation Data:</strong> Questions you ask the chatbot and the responses provided</li>
              <li><strong>Feedback:</strong> Any feedback or comments you submit through the platform</li>
            </ul>

            <h3 className="text-xl font-medium text-gray-800 mb-3">1.2 Information Collected Automatically</h3>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Usage Data:</strong> Session timestamps, duration, frequency of use, and interaction patterns</li>
              <li><strong>Technical Data:</strong> IP address, browser type, device information, and operating system</li>
              <li><strong>Document Access Logs:</strong> Records of which documents you queried and accessed</li>
              <li><strong>Analytics Data:</strong> Aggregated and anonymized usage statistics</li>
            </ul>
          </section>

          {/* 2. How We Use Your Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">2. How We Use Your Information</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Service Delivery:</strong> Provide accurate chatbot responses and access to investor documents</li>
              <li><strong>Service Improvement:</strong> Analyze usage patterns to enhance chatbot accuracy and user experience</li>
              <li><strong>Business Insights:</strong> Understand investor interests and engagement levels (aggregated data only)</li>
              <li><strong>Security:</strong> Detect and prevent fraud, abuse, and unauthorized access</li>
              <li><strong>Legal Compliance:</strong> Comply with applicable laws, regulations, and legal processes</li>
              <li><strong>Communication:</strong> Send important updates about the service or your account (rare, service-related only)</li>
            </ul>
          </section>

          {/* 3. Data Sharing and Disclosure */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">3. Data Sharing and Disclosure</h2>

            <h3 className="text-xl font-medium text-gray-800 mb-3">3.1 Third-Party Service Providers</h3>
            <p className="mb-3 text-gray-700">We share data with the following service providers who process data on our behalf:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Anthropic:</strong> AI provider that processes your questions to generate chatbot responses</li>
              <li><strong>Clerk:</strong> Authentication provider that manages user accounts and login sessions</li>
              <li><strong>Google Cloud Platform:</strong> Infrastructure provider that hosts the application and stores data</li>
            </ul>
            <p className="mb-4 text-gray-700">These providers are contractually obligated to protect your data and use it only for the specified purposes.</p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">3.2 Legal Requirements</h3>
            <p className="mb-4 text-gray-700">We may disclose your information if required by law, court order, subpoena, or government request, or to protect our rights, property, or safety.</p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">3.3 Business Transfers</h3>
            <p className="mb-4 text-gray-700">If Section is involved in a merger, acquisition, or sale of assets, your information may be transferred to the acquiring entity.</p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">3.4 Data Sale</h3>
            <p className="mb-4 text-gray-700"><strong>We do not sell your personal information to third parties.</strong></p>
          </section>

          {/* 4. Your California Privacy Rights (CCPA) */}
          <section className="mb-8 bg-purple-50 p-6 rounded-lg">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">4. Your California Privacy Rights (CCPA)</h2>
            <p className="mb-4 text-gray-700">If you are a California resident, you have the following rights under the California Consumer Privacy Act:</p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">4.1 Right to Know</h3>
            <p className="mb-4 text-gray-700">You have the right to request disclosure of the personal information we collect, use, disclose, and sell about you.</p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">4.2 Right to Delete</h3>
            <p className="mb-4 text-gray-700">You have the right to request deletion of your personal information, subject to certain exceptions (e.g., legal obligations, fraud prevention).</p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">4.3 Right to Opt-Out</h3>
            <p className="mb-4 text-gray-700">You have the right to opt-out of the sale of your personal information. <strong>Section does not sell personal information.</strong></p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">4.4 Right to Non-Discrimination</h3>
            <p className="mb-4 text-gray-700">We will not discriminate against you for exercising your CCPA rights.</p>

            <h3 className="text-xl font-medium text-gray-800 mb-3">4.5 How to Exercise Your Rights</h3>
            <p className="mb-2 text-gray-700">To exercise any of these rights:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Email us at: <a href="mailto:privacy@section.com" className="text-purple-600 hover:underline">privacy@section.com</a></li>
              <li>Use the "Request Data Deletion" feature in your account settings</li>
            </ul>
            <p className="text-gray-700">We will respond to verified requests within 45 days as required by law.</p>
          </section>

          {/* 5. Data Security */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">5. Data Security</h2>
            <p className="mb-4 text-gray-700">We implement industry-standard security measures to protect your information:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Encryption:</strong> Data is encrypted in transit (TLS/HTTPS) and at rest</li>
              <li><strong>Access Controls:</strong> Strict role-based access controls limit who can view your data</li>
              <li><strong>Authentication:</strong> Secure authentication via Clerk with multi-factor authentication support</li>
              <li><strong>Monitoring:</strong> Continuous monitoring for suspicious activity and security threats</li>
              <li><strong>Regular Audits:</strong> Periodic security assessments and vulnerability testing</li>
            </ul>
            <p className="text-gray-700">However, no system is 100% secure. We cannot guarantee absolute security of your data.</p>
          </section>

          {/* 6. Data Retention */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">6. Data Retention</h2>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li><strong>Chat History:</strong> Retained for 2 years after your last login, or until you request deletion</li>
              <li><strong>Account Information:</strong> Retained while your account is active and for 2 years after account closure</li>
              <li><strong>Analytics Data:</strong> Aggregated and anonymized analytics may be retained indefinitely for business insights</li>
              <li><strong>Session Logs:</strong> Deleted after 90 days for security and troubleshooting purposes</li>
            </ul>
          </section>

          {/* 7. International Users */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">7. International Users</h2>
            <p className="mb-4 text-gray-700">Section is based in the United States. If you access the service from outside the U.S., your information will be transferred to, stored in, and processed in the United States. By using the service, you consent to this transfer.</p>
            <p className="text-gray-700">For users in the European Economic Area (EEA), we comply with applicable data protection laws including GDPR.</p>
          </section>

          {/* 8. Children's Privacy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">8. Children's Privacy</h2>
            <p className="text-gray-700">Our service is not intended for individuals under 18 years of age. We do not knowingly collect personal information from children. If we discover that a child has provided personal information, we will delete it immediately.</p>
          </section>

          {/* 9. Changes to This Privacy Policy */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">9. Changes to This Privacy Policy</h2>
            <p className="mb-4 text-gray-700">We may update this Privacy Policy from time to time. When we make material changes, we will:</p>
            <ul className="list-disc pl-6 mb-4 text-gray-700">
              <li>Update the "Last Updated" date at the top of this policy</li>
              <li>Notify you via email (if you have an account)</li>
              <li>Display a prominent notice in the application</li>
            </ul>
            <p className="text-gray-700">Continued use of the service after changes constitutes acceptance of the updated policy.</p>
          </section>

          {/* 10. Contact Information */}
          <section className="mb-8">
            <h2 className="text-2xl font-semibold text-gray-900 mb-4">10. Contact Information</h2>
            <p className="mb-4 text-gray-700">If you have questions, concerns, or requests regarding this Privacy Policy or your personal data:</p>
            <div className="bg-gray-50 p-4 rounded border border-gray-200">
              <p className="font-medium text-gray-900">Section (ProfAI Inc.)</p>
              <p className="text-gray-700">Privacy Compliance Team</p>
              <p className="text-gray-700">Email: <a href="mailto:privacy@section.com" className="text-purple-600 hover:underline">privacy@section.com</a></p>
              <p className="text-gray-700 mt-2">For data deletion requests, please allow up to 45 days for processing as required by CCPA.</p>
            </div>
          </section>
        </div>

        {/* Footer */}
        <div className="mt-12 pt-8 border-t border-gray-200 text-center">
          <button
            onClick={() => window.history.back()}
            className="px-6 py-3 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            Back to App
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
